import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FilePlus, Upload, Sparkle, MagicWand, ListBullets, Gavel, CaretRight,
  FloppyDisk, PaperPlaneTilt, Package, Buildings, CurrencyDollar, Calendar,
  Tag, Stack, CheckCircle, Clock, Warning, FileDoc, X, Lightning, FilePdf,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import RichEditor, { htmlToPlain } from "@/components/RichEditor";
import { openContractPdfPreview } from "@/lib/pdfPreview";
import api from "@/lib/api";
import { toast } from "sonner";

const DOC_TYPE_CANON = {
  nda: "NDA / Confidentiality",
  msa: "Master Services Agreement",
  sow: "Statement of Work",
  employment: "Employment Contract",
  consulting: "Consulting Agreement",
  vendor: "Vendor / Supplier Contract",
  subscription: "SaaS Subscription",
  lease: "Real-estate Lease",
  loan: "Loan Agreement",
  insurance_proposal: "Insurance Proposal",
  insurance_policy: "Insurance Policy",
  partnership: "Partnership / JV",
  licensing: "Licensing / IP",
  purchase: "Purchase Order",
};

export default function ContractStudio() {
  const nav = useNavigate();
  const [qp] = useSearchParams();
  const bpFromUrl = qp.get("boilerplate");
  const idFromUrl = qp.get("id");

  // --- listing data
  const [templates, setTemplates] = useState([]);
  const [boilerplates, setBoilerplates] = useState([]);
  const [docTypeMappings, setDocTypeMappings] = useState({});
  const [docTypes, setDocTypes] = useState([]);

  // --- contract being built
  const [contractId, setContractId] = useState(null);
  const [form, setForm] = useState({
    title: "",
    counterparty: "",
    category: "",           // doc_type — drives workflow routing
    contract_type: "service_agreement",
    value_amount: 0,
    value_currency: "USD",
    effective_date: "",
    expiration_date: "",
    content: "",
    tags: [],
    file_storage: null,
  });
  const [sourceBoilerplate, setSourceBoilerplate] = useState(null);

  const [savingDraft, setSavingDraft] = useState(false);
  const [formatting, setFormatting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // "saving" | "saved" | null
  const [tenantIndustry, setTenantIndustry] = useState("general");
  const [onlyMyIndustry, setOnlyMyIndustry] = useState(true);

  // Fetch tenant industry up-front so we can filter templates + boilerplates by default
  useEffect(() => {
    api.get("/compliance/tenant").then(({ data }) => {
      setTenantIndustry(data?.config?.industry || "general");
    }).catch(() => {});
    api.get("/doc-types").then(({ data }) => {
      setDocTypes(data.doc_types || []);
      setDocTypeMappings(data.mappings || {});
    }).catch(() => {});
  }, []);

  // Reload templates & boilerplates when the industry-filter toggle or tenantIndustry changes
  useEffect(() => {
    const indParam = onlyMyIndustry ? tenantIndustry : "all";
    api.get("/templates", { params: { industry: indParam } }).then(({ data }) => setTemplates(data.templates || []));
    const bpParams = onlyMyIndustry ? { scope: "industry" } : {};
    api.get("/boilerplates", { params: bpParams }).then(({ data }) => setBoilerplates(data.boilerplates || [])).catch(() => {});
  }, [onlyMyIndustry, tenantIndustry]);

  // If URL has ?id=…, hydrate an existing draft so refresh preserves state
  useEffect(() => {
    if (!idFromUrl || contractId === idFromUrl) return;
    api.get(`/contracts/${idFromUrl}`).then(({ data }) => {
      setContractId(data.id);
      setForm((prev) => ({
        ...prev,
        title: data.title || "",
        counterparty: data.counterparty || "",
        category: data.category || "",
        contract_type: data.contract_type || prev.contract_type,
        value_amount: data.value_amount || 0,
        value_currency: data.value_currency || "USD",
        effective_date: data.effective_date || "",
        expiration_date: data.expiration_date || "",
        content: data.content_html || mdToHtml(data.content || ""),
        tags: data.tags || [],
        file_storage: data.file_storage || null,
      }));
    }).catch(() => toast.error("Draft not found"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idFromUrl]);

  // Pre-select a boilerplate via URL ?boilerplate=id
  useEffect(() => {
    if (!bpFromUrl || !boilerplates.length) return;
    const bp = boilerplates.find((b) => b.id === bpFromUrl);
    if (bp) applyBoilerplate(bp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bpFromUrl, boilerplates]);

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e?.target?.value ?? e }));

  const applyBoilerplate = (bp) => {
    let content = bp.content || "";
    (bp.fields || []).forEach((f) => {
      if (f.example && f.token) content = content.replace(f.token, `${f.example}`);
    });
    setSourceBoilerplate(bp);
    setForm((prev) => ({
      ...prev,
      title: prev.title || bp.title || bp.name,
      content: mdToHtml(content),
      category: prev.category || bp.category || bp.doc_type || "",
    }));
    toast.success(`"${bp.title || bp.name}" applied`);
  };

  const applyTemplate = (t) => {
    setSourceBoilerplate(null);
    setForm((prev) => ({
      ...prev,
      title: prev.title || t.title,
      content: mdToHtml(t.content),
      contract_type: t.category,
      category: prev.category || t.category || "",
    }));
    toast.success(`${t.title} loaded`);
  };

  const formatAI = async () => {
    if (!form.content || !htmlToPlain(form.content).trim()) {
      toast.error("Nothing to format yet");
      return;
    }
    setFormatting(true);
    try {
      const { data } = await api.post("/ai/format-contract", { text: htmlToPlain(form.content) });
      setForm((prev) => ({ ...prev, content: mdToHtml(data.formatted || data.content || "") }));
      toast.success("AI formatted your contract");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI formatting failed"); }
    finally { setFormatting(false); }
  };

  const saveDraft = useCallback(async () => {
    if (!form.title?.trim()) { toast.error("Title is required"); return null; }
    setSavingDraft(true);
    setAutoSaveStatus("saving");
    try {
      const payload = {
        ...form,
        content: htmlToPlain(form.content),
        content_html: form.content,
        value_amount: Number(form.value_amount) || 0,
        status: "draft",
      };
      let cid = contractId;
      if (!cid) {
        const { data } = await api.post("/contracts", payload);
        cid = data.id;
        setContractId(cid);
        // Sync URL so refresh reloads this draft; use replace so the user can still navigate back to previous page
        nav(`/app/studio?id=${cid}`, { replace: true });
      } else {
        await api.patch(`/contracts/${cid}`, payload);
      }
      setAutoSaveStatus("saved");
      setTimeout(() => setAutoSaveStatus(null), 2000);
      return cid;
    } catch (e) {
      setAutoSaveStatus(null);
      toast.error(e?.response?.data?.detail || "Save failed");
      return null;
    } finally { setSavingDraft(false); }
  }, [form, contractId, nav]);

  const submitForApproval = async () => {
    if (!form.category) { toast.error("Pick a document type before submitting"); return; }
    const cid = await saveDraft();
    if (!cid) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/contracts/${cid}/submit-for-approval`, {});
      toast.success(`Submitted — routing via "${data.workflow?.name}"`);
      nav(`/app/workflows/instances/${data.instance.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const previewPdf = async () => {
    // Auto-save so the PDF reflects the freshest content
    const cid = await saveDraft();
    if (cid) openContractPdfPreview(cid);
  };

  // --- workflow preview based on current category selection
  const linkedWorkflowMapping = form.category ? docTypeMappings[form.category] : null;

  const filledPct = useMemo(() => {
    const mandatory = ["title", "counterparty", "category", "content"];
    const filled = mandatory.filter((k) => {
      const v = form[k];
      if (k === "content") return v && htmlToPlain(v).trim().length > 30;
      return v && String(v).trim();
    }).length;
    return Math.round((filled / mandatory.length) * 100);
  }, [form]);

  return (
    <PageShell
      title={contractId ? "Edit contract · draft" : "New Contract"}
      subtitle={sourceBoilerplate ? `From boilerplate · ${sourceBoilerplate.title || sourceBoilerplate.name}` : "Author, save as draft, and submit for approval"}
      right={
        <div className="flex items-center gap-2">
          <AutoSaveIndicator status={autoSaveStatus} />
          <Button variant="ghost" onClick={previewPdf} disabled={savingDraft || !form.title?.trim()} data-testid="preview-pdf-btn" className="gap-2">
            <FilePdf size={14} weight="duotone" /> Preview PDF
          </Button>
          <Button variant="outline" onClick={saveDraft} disabled={savingDraft} data-testid="save-draft-btn" className="gap-2">
            <FloppyDisk size={14} /> Save draft
          </Button>
          <Button onClick={submitForApproval} disabled={submitting || !form.category} data-testid="submit-approval-btn" className="gap-2">
            <PaperPlaneTilt size={14} weight="fill" /> Submit for approval
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        {/* LEFT — source picker */}
        <aside className="xl:col-span-3 space-y-4">
          <GlassCard className="p-4" data-testid="studio-left-rail">
            <div className="flex items-center justify-between mb-2">
              <div className="overline">Start from</div>
              <button
                type="button"
                onClick={() => setOnlyMyIndustry((v) => !v)}
                data-testid="industry-filter-toggle"
                title={`Toggle industry filter · currently ${onlyMyIndustry ? `"${tenantIndustry}"` : "All industries"}`}
                className={`text-[10px] px-2 py-0.5 rounded-full border transition ${onlyMyIndustry ? "bg-[var(--surface-strong)] border-[var(--accent)] text-[var(--accent)]" : "bg-[var(--surface)] border-[var(--border)] text-[var(--text-3)] hover:text-[var(--text-2)]"}`}
              >
                {onlyMyIndustry ? `My industry · ${tenantIndustry}` : "All industries"}
              </button>
            </div>
            <Tabs defaultValue="boilerplates">
              <TabsList className="grid grid-cols-3 w-full h-9">
                <TabsTrigger value="boilerplates" data-testid="src-tab-boilerplates" className="text-[11px]">Mine</TabsTrigger>
                <TabsTrigger value="templates" data-testid="src-tab-templates" className="text-[11px]">Library</TabsTrigger>
                <TabsTrigger value="blank" data-testid="src-tab-blank" className="text-[11px]">Blank</TabsTrigger>
              </TabsList>

              <TabsContent value="boilerplates" className="mt-3">
                {boilerplates.length === 0 ? (
                  <EmptyHint icon={Stack} text="No custom boilerplates yet." cta="Create one" to="/app/boilerplates" />
                ) : (
                  <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                    {boilerplates.map((bp) => (
                      <SourceCard
                        key={bp.id}
                        title={bp.title || bp.name}
                        subtitle={`${(bp.fields || []).length} fields · ${bp.category || "uncategorised"}`}
                        workflow={docTypeMappings[bp.category]?.workflow_id}
                        workflows={docTypes}
                        onClick={() => applyBoilerplate(bp)}
                        testId={`src-bp-${bp.id}`}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="templates" className="mt-3">
                <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                  {templates.slice(0, 30).map((t, i) => (
                    <SourceCard
                      key={i}
                      title={t.title}
                      subtitle={`${t.industry} · ${t.category}`}
                      onClick={() => applyTemplate(t)}
                      testId={`src-tpl-${i}`}
                    />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="blank" className="mt-3">
                <Button variant="outline" className="w-full h-24 gap-2 flex-col" onClick={() => setForm((p) => ({ ...p, content: "" }))} data-testid="start-blank-btn">
                  <FilePlus size={20} /> Start with a blank canvas
                </Button>
              </TabsContent>
            </Tabs>
          </GlassCard>

          <GlassCard className="p-4">
            <div className="overline mb-2">Completion</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-[var(--surface-2)] overflow-hidden">
                <motion.div className="h-full bg-[var(--accent)]" initial={{ width: 0 }} animate={{ width: `${filledPct}%` }} />
              </div>
              <span className="text-xs tabular-nums font-medium">{filledPct}%</span>
            </div>
            <div className="text-[11px] text-[var(--text-3)] mt-1">Title · Counterparty · Doc type · Content</div>
          </GlassCard>
        </aside>

        {/* CENTER — editor */}
        <section className="xl:col-span-6">
          <GlassCard className="p-4 mb-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-[var(--text-3)]">Title</Label>
                <Input value={form.title} onChange={set("title")} placeholder="e.g. Acme MSA — Q2 2026" data-testid="contract-title" className="mt-1 h-11 text-base" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[var(--text-3)]">Counterparty</Label>
                <Input value={form.counterparty} onChange={set("counterparty")} placeholder="Acme Corp" data-testid="contract-counterparty" className="mt-1" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[var(--text-3)]">Document type</Label>
                <Select value={form.category || "__none__"} onValueChange={(v) => setForm((p) => ({ ...p, category: v === "__none__" ? "" : v }))}>
                  <SelectTrigger className="mt-1" data-testid="contract-doctype"><SelectValue placeholder="Choose a type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— none —</SelectItem>
                    {(docTypes.length ? docTypes : Object.entries(DOC_TYPE_CANON).map(([id, label]) => ({ id, label }))).map((dt) => (
                      <SelectItem key={dt.id} value={dt.id}>{dt.label || DOC_TYPE_CANON[dt.id] || dt.id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </GlassCard>

          <RichEditor
            value={form.content}
            onChange={(html) => setForm((p) => ({ ...p, content: html }))}
            onFormatAI={formatAI}
            onInsertVariable={() => {
              const v = window.prompt("Variable name (letters, digits, underscores)", "client_name");
              if (!v) return;
              const token = `{{${v.replace(/[^a-zA-Z0-9_]/g, "_")}}}`;
              setForm((p) => ({ ...p, content: (p.content || "") + ` ${token}` }));
            }}
            placeholder={sourceBoilerplate ? "Select sections to edit…" : "Start typing, or pick a template on the left…"}
            testId="contract-editor"
          />

          <div className="flex items-center justify-between text-[11px] text-[var(--text-3)] mt-2 px-1">
            <div>~{htmlToPlain(form.content).split(/\s+/).filter(Boolean).length} words</div>
            {formatting && <div className="flex items-center gap-1"><Lightning size={11} className="animate-pulse" /> AI formatting…</div>}
          </div>
        </section>

        {/* RIGHT — metadata + workflow preview */}
        <aside className="xl:col-span-3 space-y-4">
          {form.category && (
            <WorkflowPreviewCard
              docType={form.category}
              mapping={linkedWorkflowMapping}
              docTypes={docTypes}
            />
          )}

          {sourceBoilerplate && sourceBoilerplate.category && docTypeMappings[sourceBoilerplate.category] && (
            <GlassCard className="p-4" data-testid="boilerplate-workflow-hint">
              <div className="flex items-center gap-2 mb-2">
                <Package size={14} weight="fill" className="text-[var(--accent)]" />
                <div className="text-xs font-medium">From boilerplate</div>
              </div>
              <div className="text-sm truncate">{sourceBoilerplate.title || sourceBoilerplate.name}</div>
              <div className="text-[11px] text-[var(--text-3)] mt-1">
                This boilerplate's document type is mapped to an approval workflow.
              </div>
            </GlassCard>
          )}

          <GlassCard className="p-4">
            <div className="overline mb-2">Contract details</div>
            <div className="space-y-3">
              <Field icon={CurrencyDollar} label="Value">
                <div className="flex gap-2">
                  <Input type="number" value={form.value_amount} onChange={set("value_amount")} data-testid="contract-value" className="flex-1" />
                  <Select value={form.value_currency} onValueChange={(v) => setForm((p) => ({ ...p, value_currency: v }))}>
                    <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["USD", "EUR", "GBP", "AED", "SAR", "INR", "SGD"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </Field>
              <Field icon={Calendar} label="Effective date">
                <Input type="date" value={form.effective_date} onChange={set("effective_date")} data-testid="contract-effective" />
              </Field>
              <Field icon={Calendar} label="Expiration">
                <Input type="date" value={form.expiration_date} onChange={set("expiration_date")} data-testid="contract-expiration" />
              </Field>
              <Field icon={Tag} label="Tags">
                <Input placeholder="comma-separated" value={(form.tags || []).join(", ")} onChange={(e) => setForm((p) => ({ ...p, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }))} data-testid="contract-tags" />
              </Field>
            </div>
          </GlassCard>
        </aside>
      </div>
    </PageShell>
  );
}

// ========== Sub-components ==========
function SourceCard({ title, subtitle, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className="w-full text-left p-3 rounded-lg bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--accent)] transition group">
      <div className="flex items-start gap-2">
        <FileDoc size={14} className="mt-0.5 text-[var(--text-3)] group-hover:text-[var(--accent)]" />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium truncate">{title}</div>
          <div className="text-[10px] text-[var(--text-3)] truncate">{subtitle}</div>
        </div>
        <CaretRight size={10} className="opacity-0 group-hover:opacity-100 transition mt-1" />
      </div>
    </button>
  );
}

function EmptyHint({ icon: Icon, text, cta, to }) {
  const nav = useNavigate();
  return (
    <div className="py-6 text-center" data-testid="src-empty-hint">
      <Icon size={22} className="mx-auto mb-2 text-[var(--text-3)]" />
      <div className="text-[11px] text-[var(--text-3)] mb-2">{text}</div>
      {cta && <Button variant="ghost" size="sm" onClick={() => nav(to)} className="text-xs">{cta} →</Button>}
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-[var(--text-3)] flex items-center gap-1"><Icon size={10} /> {label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function AutoSaveIndicator({ status }) {
  if (status === "saving") return <span className="text-[11px] text-[var(--text-3)] flex items-center gap-1" data-testid="autosave-saving"><Clock size={11} /> Saving…</span>;
  if (status === "saved")   return <span className="text-[11px] text-emerald-500 flex items-center gap-1" data-testid="autosave-saved"><CheckCircle size={11} weight="fill" /> Saved</span>;
  return null;
}

function WorkflowPreviewCard({ docType, mapping, docTypes }) {
  const nav = useNavigate();
  const dtLabel = docTypes.find((d) => d.id === docType)?.label || DOC_TYPE_CANON[docType] || docType;
  return (
    <GlassCard className="p-4" data-testid="workflow-preview-card">
      <div className="flex items-center gap-2 mb-2">
        <Gavel size={14} weight="fill" className="text-[var(--accent)]" />
        <div className="text-xs font-medium">Approval routing</div>
      </div>
      <div className="text-[11px] text-[var(--text-3)] mb-2">Document type</div>
      <Badge variant="outline" className="mb-3">{dtLabel}</Badge>
      {mapping?.workflow_id ? (
        <>
          <div className="text-[11px] text-[var(--text-3)] mb-1">Will follow workflow</div>
          <div className="px-3 py-2 rounded-md bg-[var(--surface-2)] border border-[var(--border)] text-sm flex items-center gap-2" data-testid="workflow-mapped">
            <Lightning size={12} weight="fill" className="text-amber-500" />
            <div className="flex-1 truncate">{mapping.workflow_id.slice(0, 10)}…</div>
            <Badge variant="outline" className="text-[9px]">{mapping.version_strategy === "pinned" ? `v${mapping.pinned_version}` : "latest"}</Badge>
          </div>
        </>
      ) : (
        <Alert data-testid="workflow-unmapped" className="p-3">
          <Warning className="w-3 h-3" />
          <AlertDescription className="text-[11px]">
            No workflow is mapped for <strong>{dtLabel}</strong>. Submitting will fail until an admin links one in{" "}
            <button className="underline" onClick={() => nav("/app/doc-types")}>Doc Types → Workflows</button>.
          </AlertDescription>
        </Alert>
      )}
    </GlassCard>
  );
}

// Rough markdown → HTML used when loading boilerplates/templates that historically stored
// plain-text with double-newline paragraphs. TipTap is forgiving and accepts minimal HTML.
function mdToHtml(md) {
  if (!md) return "";
  if (/<[a-z][\s\S]*>/i.test(md)) return md; // already HTML
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let buf = [];
  const flush = () => { if (buf.length) { out.push(`<p>${buf.join(" ").replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>")}</p>`); buf = []; } };
  for (const raw of lines) {
    const l = raw.trim();
    if (!l) { flush(); continue; }
    const h = l.match(/^(#{1,6})\s+(.*)/);
    if (h) { flush(); out.push(`<h${h[1].length}>${h[2]}</h${h[1].length}>`); continue; }
    if (/^\s*[-*]\s+/.test(l)) { flush(); out.push(`<ul><li>${l.replace(/^\s*[-*]\s+/, "")}</li></ul>`); continue; }
    buf.push(l);
  }
  flush();
  return out.join("\n");
}
